// Położenie sklepów: odległości, położenie z próbek wizyt i rozpoznawanie sklepu, w którym jesteś.
import type { Id } from './types';
import { compareIds } from './order/positions';

export interface LatLng {
  lat: number;
  lng: number;
}

export interface StoreLocation extends LatLng {
  source: 'visits' | 'address';
}

/**
 * Skąd są współrzędne sklepu (pole LWW stores.geoSource): `address` — z geokodowania adresu, niepotwierdzone;
 * `here` — z telefonu w sklepie albo adres potwierdzony „To tu”; `rejected` — „To nie tu”, współrzędnych brak
 * i adresu nie geokodujemy ponownie (zmiana adresu czyści pole).
 */
export type GeoSource = 'address' | 'here' | 'rejected';

/** Pola wiersza stores potrzebne do położenia. */
export interface StoreCoords {
  lat?: number | null;
  lng?: number | null;
  geoSource?: GeoSource | null;
}

/** Próbka położenia z wizyty (pola wiersza visits). */
export interface VisitSample {
  lat?: number | null;
  lng?: number | null;
  accM?: number | null;
  startedMs: number;
}

const EARTH_RADIUS_M = 6_371_000;
/** Położenie sklepu to mediana tylu ostatnich próbek z wizyt. */
export const LOCATION_SAMPLES = 10;
/** Próbki mniej dokładne niż tyle metrów nic nie mówią o sklepie. */
export const MAX_SAMPLE_ACCURACY_M = 100;
/** Sklep jest „tutaj”, gdy jest bliżej niż max(150 m, 1,5 × dokładność pozycji). */
export const NEARBY_MIN_M = 150;
export const NEARBY_ACCURACY_FACTOR = 1.5;
/** Próbkę bierzemy, gdy tyle ostatnich odhaczeń wizyty… */
export const SAMPLE_WINDOW = 3;
/** …rozkłada się na co najmniej tyle (hurtem w domu po zakupach — nie). */
export const SAMPLE_MIN_SPAN_MS = 60_000;
/** Zaufane położenie z wizyt wymaga co najmniej tylu próbek. */
export const TRUSTED_MIN_SAMPLES = 2;

const rad = (deg: number) => (deg * Math.PI) / 180;

function isLatLng(lat: unknown, lng: unknown): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat as number) <= 90 && Math.abs(lng as number) <= 180;
}

/** Odległość po powierzchni Ziemi (haversine), w metrach. */
export function distanceM(a: LatLng, b: LatLng): number {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 === 1 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

/** Ostatnie dokładne próbki z wizyt (najnowsze pierwsze). */
function recentSamples(visits: readonly VisitSample[]): VisitSample[] {
  return visits
    .filter((v) => isLatLng(v.lat, v.lng) && Number.isFinite(v.accM) && v.accM! <= MAX_SAMPLE_ACCURACY_M)
    .sort((a, b) => b.startedMs - a.startedMs)
    .slice(0, LOCATION_SAMPLES);
}

/** Mediana osobno szerokości i długości — odporna na pojedynczą złą próbkę. */
function medianOf(samples: readonly VisitSample[]): LatLng {
  return { lat: median(samples.map((v) => v.lat!)), lng: median(samples.map((v) => v.lng!)) };
}

function coordsOf(store: StoreCoords): LatLng | null {
  return isLatLng(store.lat, store.lng) ? { lat: store.lat!, lng: store.lng! } : null;
}

/**
 * Położenie sklepu: mediana ostatnich próbek z wizyt; bez dokładnych próbek współrzędne sklepu
 * (z adresu albo z telefonu, ale nie odrzucone „To nie tu”); null, gdy nie ma żadnych.
 */
export function storeLocation(store: StoreCoords, visits: readonly VisitSample[]): StoreLocation | null {
  const samples = recentSamples(visits);
  if (samples.length > 0) return { ...medianOf(samples), source: 'visits' };
  const coords = store.geoSource === 'rejected' ? null : coordsOf(store);
  return coords ? { ...coords, source: 'address' } : null;
}

/**
 * Położenie, któremu ufamy przy odrzucaniu odległych próbek: mediana co najmniej 2 próbek z wizyt,
 * inaczej współrzędne sklepu tylko wtedy, gdy pochodzą z telefonu lub potwierdzenia (geoSource 'here').
 * Samo geokodowanie adresu może wskazać inne miejsce, więc się nie liczy.
 */
export function trustedLocation(store: StoreCoords, visits: readonly VisitSample[]): LatLng | null {
  const samples = recentSamples(visits);
  if (samples.length >= TRUSTED_MIN_SAMPLES) return medianOf(samples);
  return store.geoSource === 'here' ? coordsOf(store) : null;
}

/** Najbliższy sklep w zasięgu pozycji telefonu (przy remisie mniejsze id) albo null. */
export function nearestStore<T extends { id: Id }>(
  pos: LatLng & { accM: number },
  stores: readonly { store: T; loc: LatLng }[],
): { store: T; distanceM: number } | null {
  const accuracy = Number.isFinite(pos.accM) ? pos.accM : 0;
  const limit = Math.max(NEARBY_MIN_M, NEARBY_ACCURACY_FACTOR * accuracy);
  let best: { store: T; distanceM: number } | null = null;
  for (const { store, loc } of stores) {
    const d = distanceM(pos, loc);
    if (!(d <= limit)) continue;
    if (!best || d < best.distanceM || (d === best.distanceM && compareIds(store.id, best.store.id) < 0)) {
      best = { store, distanceM: d };
    }
  }
  return best;
}

/**
 * Czy po tym odhaczeniu zapisać próbkę położenia wizyty. `checks` = odhaczenia tej wizyty z tego urządzenia
 * w kolejności (razem z właśnie zrobionym). Prawda, gdy 3 ostatnie rozkładają się na ≥ 60 s — od trzeciego
 * odhaczenia wzwyż, więc nieudany odczyt GPS ponawia się przy kolejnych. Wołający pomija wizyty z próbką.
 */
export function shouldSample(checks: readonly { wallMs: number }[]): boolean {
  if (checks.length < SAMPLE_WINDOW) return false;
  const times = checks.slice(-SAMPLE_WINDOW).map((c) => c.wallMs);
  return Math.max(...times) - Math.min(...times) >= SAMPLE_MIN_SPAN_MS;
}
